import React, { useState, useCallback, useContext } from "react";
import { Dialog, DialogContent, DialogActions, Button as MuiButton, AppBar, Toolbar, Typography, IconButton, Box, useTheme, styled, Divider, Stack } from "@mui/material";
import { Close as CloseIcon, Print as PrintIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatTimeOnly } from "../../../helpers/utils";
import { kontram } from "../../../../theme/muiTheme";

const AutoZeroPrintVersion = ({
  isOpen,
  onClose,
  values,
  systemName,
  loading,
  t
}) => {

  console.log("values in print version:", values);

  const handlePrint = () => {
    window.print();
  };

  const theme = useTheme();

  const StyledTypography = styled(Typography)({
    color: 'black'
  });

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      sx={{ backgroundColor: 'white' }}
    >
      <AppBar sx={{
        '@media print': {
          display: 'none',
        },
        position: 'relative',
        backgroundColor: kontram.primary
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            AutoZero Report - Print View
          </Typography>
          <MuiButton
            color="inherit"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
          >
            Print
          </MuiButton>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ padding: 3, minHeight: '100vh', backgroundColor: 'white' }}>
        <StyledTypography
          variant="h5" sx={{ textAlign: 'center' }}
        >
          AutoZero Reports
        </StyledTypography>
        <StyledTypography
          variant="h6" sx={{ mb: 2, textAlign: 'center' }}
        >
          {systemName}
        </StyledTypography>

        {/* render reports */}
        {!loading &&
          values &&
          values.length > 0 &&
          values.map((val, idx) => (
            <Box
              key={`print-${val}${idx}`}
              sx={{
                border: `2px solid ${theme.palette.grey[700]}`,
                borderRadius: 1,
                padding: 2,
                marginBottom: 3,
                '@media print': {
                  pageBreakAfter: 'always',
                  '&:last-child': {
                    pageBreakAfter: 'avoid'
                  }
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Date:
                  </StyledTypography>
                  <StyledTypography >
                    {formatDateTime(val.ReportTimeStamp)}
                  </StyledTypography>
                </Stack>
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Started:
                  </StyledTypography>
                  <StyledTypography >
                    {formatTimeOnly(val.StartTime)}
                  </StyledTypography>
                </Stack>
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Duration:
                  </StyledTypography>
                  <StyledTypography>
                    {Math.floor(val.Duration / 60) +
                      "m " +
                      Math.floor(val.Duration % 60) +
                      "s"}
                  </StyledTypography>
                </Stack>
                <Stack direction="column" sx={{ flex: 1 }}>
                  <StyledTypography sx={{ fontWeight: 'bold' }}>
                    Result:
                  </StyledTypography>
                  <Typography
                    // color={
                    //   val.State === "Warning"
                    //     ? theme.palette.warning.dark
                    //     : val.State.includes("Failed")
                    //       ? theme.palette.error.dark
                    //       : theme.palette.success.dark
                    // }
                    backgroundColor={
                      val.State === "Warning"
                        ? theme.palette.warning.dark
                        : val.State.includes("Failed")
                          ? theme.palette.error.dark
                          : theme.palette.success.dark
                    }
                    sx={{ px: 1, borderRadius: 1 }}
                  >
                    {t(val.State)}
                  </Typography>
                </Stack>
              </Box>
              <Divider color={theme.palette.common.black} sx={{ my: 2 }} />
              {/* Component Results */}
              <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                {val.ComponentResults &&
                  Object.entries(val.ComponentResults).map(([key, component], compIdx) => (
                    <Box
                      key={`print-component-${idx}-${compIdx}`}
                      sx={{ border: `1px solid ${theme.palette.grey[500]}`, padding: 1, minWidth: "160px" }}
                    >
                      <Stack direction="column" sx={{ mb: 1 }}>
                        <StyledTypography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                          {key}
                        </StyledTypography>
                        <Divider color={theme.palette.common.black} />
                        <StyledTypography sx={{ fontSize: '0.7rem', color: theme.palette.grey[600] }}>
                          {t("PreCal")}
                        </StyledTypography>
                        <StyledTypography>
                          {component.PreCalValue.toFixed(2)} {component.Unit}
                        </StyledTypography>
                        <StyledTypography sx={{ fontSize: '0.7rem', color: theme.palette.grey[600] }}>
                          {t("Delta")}
                        </StyledTypography>
                        <StyledTypography>
                          {(component.PostCalValue - component.PreCalValue).toFixed(2)} {component.Unit}
                        </StyledTypography>
                        <StyledTypography sx={{ fontSize: '0.7rem', color: theme.palette.grey[600] }}>
                          {t("PostCal")}
                        </StyledTypography>
                        <Typography color={theme.palette.grey[700]}>
                          {component.PostCalValue.toFixed(2)} {component.Unit}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
              </Box>
              {/* end of component results */}
              <Divider color={theme.palette.common.black} sx={{ my: 2 }} />
              {/* Extra info, about alarms and so on*/}
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                {val.ActiveAlarms.length > 0 && (
                  <Stack direction="column" sx={{ flex: 1 }}>
                    <StyledTypography sx={{ fontWeight: 'bold', mb: 1 }}>
                      ACTIVE ALARMS:
                    </StyledTypography>
                    {val.ActiveAlarms.map((alarm, alarmIdx) => (
                      <StyledTypography key={`print-alarm-${idx}-${alarmIdx}`}>
                        - {alarm}
                      </StyledTypography>
                    ))}
                  </Stack>
                )}
                {val.Diagnostics && (
                  <Stack sx={{ flex: 1 }}>

                    <StyledTypography sx={{ fontWeight: 'bold', mb: 1 }}>
                      DIAGNOSTICS:
                    </StyledTypography>
                    {Object.entries(val.Diagnostics).map(([key, diagnostic], diagIdx) => (
                      <Stack direction="row" key={`print-diagnostic-${idx}-${diagIdx}`}  >
                        <StyledTypography sx={{ minWidth: '265px' }}>
                          - {key}:
                        </StyledTypography>
                        <StyledTypography >
                          {diagnostic.DiagnosticsValue.toFixed(3)} {diagnostic.DiagnosticsUnit}
                        </StyledTypography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
              {/* end of extra info */}
            </Box>
            //end of individual report box 
          ))}
      </DialogContent>
    </Dialog >
  )
}
export default AutoZeroPrintVersion